import GoogleTagManagerProvider from "./gtm";
import KlaviyoProvider from "./klaviyo";
import MixpanelProvider from "./mixpanel";
import { ICheckout, ICheckoutTotals, ILineItem } from "../types/types";

export interface IAnalyticsProvider {
  trackEvent(event: string, data: Record<string, any>): Promise<void>;
}

let instance: TrackingService | null = null;

export function isDefaultVariantTitle(value: unknown): boolean {
  return String(value || "").trim().toLowerCase() === "default title";
}

export function cleanItemName(value: unknown): string {
  return String(value || "").replace(/\s+-\s+Default Title$/i, "").trim();
}

export function cleanVariantName<T>(value: T): T | string {
  return isDefaultVariantTitle(value) ? "" : value;
}

function cleanLineItemProductNames<T extends Pick<ILineItem, "productTitle" | "variantTitle">>(lineItem: T): T {
  return {
    ...lineItem,
    productTitle: cleanItemName(lineItem.productTitle),
    variantTitle: cleanVariantName(lineItem.variantTitle),
  };
}

class TrackingService {
  private providers: IAnalyticsProvider[] = [];
  private googleTagManagerProvider = new GoogleTagManagerProvider();

  constructor() {
    if (instance) {
      throw new Error("You can only create one instance!");
    }
    this.addProvider(new KlaviyoProvider());
    this.addProvider(new MixpanelProvider());
    this.addProvider(this.googleTagManagerProvider);
    instance = this;
  }

  addProvider(provider: IAnalyticsProvider): void {
    this.providers.push(provider);
  }

  async trackEvent(event: string, data: Record<string, any>): Promise<void> {
    const eventData = this.cleanTrackingData(data);
    const trackingPromises = this.providers.map((provider) => {
      try {
        return provider.trackEvent(event, eventData);
      } catch (e: unknown) {
        console.error(e);
        return Promise.reject();
      }
    });
    await Promise.all(trackingPromises);
  }

  async trackGtmEvent(event: string, data: { checkout: ICheckout, totals: ICheckoutTotals }): Promise<void> {
    await this.googleTagManagerProvider.trackEvent(event, this.cleanTrackingData(data));
  }

  private cleanTrackingData<T extends Record<string, any>>(data: T): T {
    if (!data?.checkout?.lineItems) {
      return data;
    }

    return {
      ...data,
      checkout: {
        ...data.checkout,
        lineItems: data.checkout.lineItems.map(cleanLineItemProductNames),
      },
    };
  }
}

const trackingService = Object.freeze(new TrackingService());
export default trackingService;
